import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../firebase";
import { subscribeAuth, waitForAuth, readAppUser } from "../services/auth";
import {
  normalizeCandidate, usernameValidationError,
  checkUsernameAvailable, renameUsername,
} from "../utils/username";

// After the first Google/email sign-in a Pop Market user picks:
//   1. Their display name (pre-filled from Google, editable)
//   2. Their username (pre-filled with the auto-generated stub, live
//      availability-checked, editable — this is the @handle other
//      users see on forum posts and can click through to /u/:username)
//   3. Whether they're here to sell popularity, buy it, or both
//   4. Optionally a WhatsApp so counterparties can reach them
//
// All four writes fan out to app_users/{uid} + Usernames/{name} so
// the public profile page stays coherent for anon browsers.

const COUNTRIES = [
  { code: "+91", label: "🇮🇳" },
  { code: "+1",  label: "🇺🇸" },
  { code: "+44", label: "🇬🇧" },
  { code: "+61", label: "🇦🇺" },
  { code: "+65", label: "🇸🇬" },
  { code: "+971",label: "🇦🇪" },
  { code: "+49", label: "🇩🇪" },
  { code: "+33", label: "🇫🇷" },
  { code: "+81", label: "🇯🇵" },
  { code: "+82", label: "🇰🇷" },
  { code: "+55", label: "🇧🇷" },
];

const PHONE_RE = /^\+[1-9]\d{7,14}$/;
function normalizePhone(local, cc) {
  const digits = String(local || "").replace(/\D+/g, "");
  if (!digits) return "";
  const ccDigits = cc.replace(/\D/g, "");
  const bare = digits.startsWith(ccDigits) ? digits.slice(ccDigits.length) : digits;
  return cc + bare;
}

const POST_AUTH_TARGET_KEY = "popmarket-post-auth-target";

export default function RolePick() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [appUser, setAppUser] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [role, setRole] = useState(null);      // "seller" | "buyer" | "both"
  const [cc, setCc] = useState("+91");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // Live availability probe: null | "checking" | "ok" | "taken" | "invalid"
  const [availability, setAvailability] = useState(null);
  const availabilityTimerRef = useRef(null);

  useEffect(() => subscribeAuth(setUser), []);

  // Hydrate the form from any existing app_users state so a returning
  // user landing here sees their current values, not blank fields.
  useEffect(() => {
    (async () => {
      try {
        // Same defensive load pattern as EditProfile — prefer the
        // already-restored session, guard waitForAuth and the doc
        // read with timeouts so we never sit at "Loading…" forever.
        let u = auth.currentUser;
        if (!u) {
          u = await Promise.race([
            waitForAuth(),
            new Promise((resolve) => setTimeout(() => resolve(null), 5000)),
          ]);
        }
        if (!u) { navigate("/signin", { replace: true }); return; }
        setUser(u);
        const doc = await Promise.race([
          readAppUser(u.uid),
          new Promise((_, reject) =>
              setTimeout(() => reject(new Error("Timed out reading your profile.")), 8000),
          ),
        ]);
        setAppUser(doc || {});
        setDisplayName(doc?.displayName || u.displayName || "");
        setUsername(doc?.username || "");
        if (doc?.popMarketRole) setRole(doc.popMarketRole);
        const wa = doc?.popMarketContact?.whatsapp;
        if (wa) {
          const match = COUNTRIES.find((c) => wa.startsWith(c.code));
          if (match) {
            setCc(match.code);
            setPhone(wa.slice(match.code.length));
          }
        }
      } catch (e) {
        setErr("Couldn't load your profile: " + (e.message || String(e)));
        setAppUser({});
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced availability check. Cancels any in-flight timer whenever
  // the input changes so we only probe once the user pauses typing.
  useEffect(() => {
    if (!user || !appUser) return;
    const normalized = normalizeCandidate(username);
    const validation = usernameValidationError(normalized);
    if (validation) {
      setAvailability("invalid");
      return;
    }
    setAvailability("checking");
    if (availabilityTimerRef.current) clearTimeout(availabilityTimerRef.current);
    availabilityTimerRef.current = setTimeout(async () => {
      try {
        const { available } = await checkUsernameAvailable(normalized, user.uid);
        setAvailability(available ? "ok" : "taken");
      } catch (_) {
        setAvailability(null);
      }
    }, 400);
    return () => {
      if (availabilityTimerRef.current) clearTimeout(availabilityTimerRef.current);
    };
  }, [username, user?.uid, appUser]);

  const normalizedUsername = normalizeCandidate(username);
  const usernameErr = usernameValidationError(normalizedUsername);

  const canContinue =
      role
      && displayName.trim().length >= 2
      && !usernameErr
      && (availability === "ok");

  const onContinue = async () => {
    if (!canContinue || !user) return;
    setErr(null);
    let e164 = "";
    if (phone.trim()) {
      e164 = normalizePhone(phone, cc);
      if (!PHONE_RE.test(e164)) {
        setErr("That phone number doesn't look right — leave it empty or fix it.");
        return;
      }
    }
    setBusy(true);
    try {
      // 1. Rename the reservation if the user changed their username.
      //    This is atomic — new claim, old released, app_users updated
      //    inside one transaction. Also stashes the public snapshot
      //    onto Usernames/{new} so /u/:username renders correctly for
      //    anon viewers.
      const publicSnapshot = {
        displayName: displayName.trim() || null,
        photoURL: user.photoURL || appUser?.photoURL || null,
        popMarketRole: role,
        ...(e164 ? { popMarketWhatsapp: e164 } : {}),
      };
      if (normalizedUsername !== appUser?.username) {
        await renameUsername(
            user.uid,
            appUser?.username || null,
            normalizedUsername,
            publicSnapshot,
        );
      } else {
        // Same name — just refresh the public snapshot on Usernames.
        try {
          await setDoc(
              doc(db, "Usernames", normalizedUsername),
              publicSnapshot,
              { merge: true },
          );
        } catch (_) { /* best-effort */ }
      }

      // 2. Persist the remaining app_users fields.
      const payload = {
        displayName: displayName.trim(),
        popMarketRole: role,
        updatedAt: serverTimestamp(),
      };
      if (e164) payload.popMarketContact = { whatsapp: e164, phone: e164 };
      await setDoc(doc(db, "app_users", user.uid), payload, { merge: true });

      const target = drainAuthTarget() || defaultTargetForRole(role);
      navigate(target, { replace: true });
    } catch (e) {
      setErr(e.message || "Couldn't save that — try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!user) {
    return (
      <div className="container" style={{ maxWidth: 480, paddingTop: 32 }}>
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div className="container" style={{ maxWidth: 640, paddingBottom: 60 }}>
      <style>{styleSheet}</style>
      <h1 style={{ marginBottom: 4 }}>Set up your profile</h1>
      <p className="muted" style={{ marginTop: 0, marginBottom: 20 }}>
        This is how other Pop Market users see you.
      </p>

      <div className="rp-field">
        <label>DISPLAY NAME</label>
        <input
          type="text"
          maxLength={40}
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Your name"
        />
      </div>

      <div className="rp-field">
        <label>USERNAME</label>
        <div style={{ position: "relative" }}>
          <span className="rp-at">@</span>
          <input
            type="text"
            maxLength={20}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="AgileWolf1"
            style={{ paddingLeft: 26 }}
            spellCheck={false}
            autoCapitalize="none"
            autoCorrect="off"
          />
        </div>
        <UsernameHint
          normalized={normalizedUsername}
          rawInput={username}
          err={usernameErr}
          availability={availability}
        />
      </div>

      <div style={{ marginTop: 24 }}>
        <div className="rp-section-label">HOW WILL YOU USE POP MARKET?</div>
        <div className="rp-cards">
          <RoleCard
            active={role === "seller"}
            onClick={() => setRole("seller")}
            icon="💎"
            title="Sell popularity"
            desc="I run rooms in BGMI / PUBG and sell pop to buyers."
            tint="#4de37c"
          />
          <RoleCard
            active={role === "buyer"}
            onClick={() => setRole("buyer")}
            icon="🎯"
            title="Buy popularity"
            desc="I want to grind pop — I'll post orders and pay sellers."
            tint="#6bb6ff"
          />
        </div>
        <button
          type="button"
          className="rp-both"
          onClick={() => setRole("both")}
          style={{
            background: role === "both" ? "rgba(227,168,87,0.14)" : "transparent",
            color: role === "both" ? "#e3a857" : "#9ca0a8",
            borderColor: role === "both" ? "#e3a857" : "rgba(255,255,255,0.10)",
          }}
        >
          🔁 Both — I sell and buy
        </button>
      </div>

      <div style={{ marginTop: 24 }}>
        <div className="rp-section-label">WHATSAPP (OPTIONAL)</div>
        <div className="rp-phone-row" style={{ marginTop: 6 }}>
          <select value={cc} onChange={(e) => setCc(e.target.value)}>
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>{c.label} {c.code}</option>
            ))}
          </select>
          <input
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            placeholder="9876543210"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>
        <p className="muted" style={{ fontSize: 11.5, marginTop: 6 }}>
          Shown on your public profile so counterparties can DM you outside chat.
          Leave empty to hide it — you can add one from your profile later.
        </p>
      </div>

      {err && <div style={{ marginTop: 12, fontSize: 13, color: "#ff6f61" }}>⚠️ {err}</div>}

      <button
        type="button"
        onClick={onContinue}
        disabled={!canContinue || busy}
        className="rp-continue"
        style={{
          background: canContinue ? "#4c6ef5" : "rgba(255,255,255,0.06)",
          color: canContinue ? "#fff" : "#5c6069",
          cursor: canContinue && !busy ? "pointer" : "not-allowed",
        }}
      >
        {busy ? "Saving…" : "Continue →"}
      </button>
    </div>
  );
}

function UsernameHint({ normalized, rawInput, err, availability }) {
  const showNormalized = rawInput && normalized && normalized !== rawInput;
  return (
    <div style={{ minHeight: 18, marginTop: 6, fontSize: 12 }}>
      {showNormalized && (
        <span style={{ color: "#9ca0a8" }}>
          Will save as <b>@{normalized}</b>
        </span>
      )}
      {err && !showNormalized && (
        <span style={{ color: "#ff6f61" }}>{err}</span>
      )}
      {!err && availability === "checking" && (
        <span style={{ color: "#9ca0a8", marginLeft: showNormalized ? 8 : 0 }}>
          Checking…
        </span>
      )}
      {!err && availability === "ok" && (
        <span style={{ color: "#4de37c", marginLeft: showNormalized ? 8 : 0 }}>
          ✓ Available
        </span>
      )}
      {!err && availability === "taken" && (
        <span style={{ color: "#ff6f61", marginLeft: showNormalized ? 8 : 0 }}>
          ✗ Already taken
        </span>
      )}
    </div>
  );
}

function RoleCard({ active, onClick, icon, title, desc, tint }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rp-card"
      style={{
        borderColor: active ? tint : "rgba(255,255,255,0.10)",
        background: active ? `${tint}18` : "rgba(255,255,255,0.03)",
      }}
    >
      <div style={{ fontSize: 34, lineHeight: 1 }}>{icon}</div>
      <div style={{ fontSize: 16, fontWeight: 800, color: "#eaeaef", marginTop: 8 }}>
        {title}
      </div>
      <div style={{ fontSize: 12.5, color: "#9ca0a8", marginTop: 4, lineHeight: 1.35 }}>
        {desc}
      </div>
      {active && (
        <div style={{
          position: "absolute", top: 8, right: 10,
          fontSize: 11, fontWeight: 900, letterSpacing: 1,
          color: tint,
        }}>✓ PICKED</div>
      )}
    </button>
  );
}

function drainAuthTarget() {
  try {
    const t = localStorage.getItem(POST_AUTH_TARGET_KEY);
    if (t) {
      localStorage.removeItem(POST_AUTH_TARGET_KEY);
      return t;
    }
  } catch (_) { /* ignore */ }
  return null;
}

function defaultTargetForRole(role) {
  if (role === "seller") return "/post";
  if (role === "buyer") return "/?role=buyer";
  return "/";
}

const styleSheet = `
  .rp-section-label {
    font-size: 11px;
    letter-spacing: 2px;
    color: #7a7d86;
    font-weight: 800;
    margin-bottom: 10px;
  }
  .rp-field { margin-bottom: 16px; }
  .rp-field label {
    display: block;
    font-size: 11px;
    letter-spacing: 2px;
    color: #7a7d86;
    font-weight: 800;
    margin-bottom: 6px;
  }
  .rp-field input {
    width: 100%;
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid rgba(255,255,255,0.12);
    background: rgba(0,0,0,0.25);
    color: #eaeaef;
    font-size: 14px;
    box-sizing: border-box;
  }
  .rp-at {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #7a7d86;
    font-size: 14px;
    pointer-events: none;
    font-weight: 700;
  }
  .rp-cards {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  .rp-card {
    position: relative;
    padding: 20px 16px 18px;
    border-radius: 14px;
    border: 1.5px solid rgba(255,255,255,0.10);
    text-align: left;
    color: inherit;
    cursor: pointer;
    transition: border-color .15s, background .15s;
  }
  .rp-card:hover { border-color: rgba(255,255,255,0.24); }
  .rp-both {
    margin-top: 12px;
    width: 100%;
    padding: 12px 16px;
    border-radius: 10px;
    border: 1.5px solid rgba(255,255,255,0.10);
    background: transparent;
    color: #9ca0a8;
    font-weight: 700;
    font-size: 13px;
    letter-spacing: 0.6px;
    cursor: pointer;
  }
  .rp-phone-row {
    display: grid;
    grid-template-columns: 100px 1fr;
    gap: 8px;
  }
  .rp-phone-row select,
  .rp-phone-row input {
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid rgba(255,255,255,0.12);
    background: rgba(0,0,0,0.25);
    color: #eaeaef;
    font-size: 14px;
    min-width: 0;
  }
  .rp-continue {
    margin-top: 20px;
    width: 100%;
    padding: 14px 18px;
    border-radius: 10px;
    border: none;
    font-size: 14px;
    font-weight: 900;
    letter-spacing: 1.4px;
    text-transform: uppercase;
  }
  @media (max-width: 500px) {
    .rp-cards { grid-template-columns: 1fr; }
    .rp-phone-row { grid-template-columns: 90px 1fr; }
  }
`;
